import { useShallow } from 'zustand/shallow'

import Badge from '@/components/atoms/Badge'
import SectionHeader from '@/components/atoms/SectionHeader'
import type { HubType } from '@/stores/HubStore'
import type { Limit } from '@/stores/LimitStore'
import useLimitStore from '@/stores/LimitStore'
import { useMobileLayoutStore, useWatchMobile } from '@/stores/MobileLayoutStore'
import { cn } from '@/util/cn'
import { CONTROL_HUB_IDS, DELUXE_HUBS_IDS, POWER_CATEGORY } from '@/util/Hardcoded'

type CategoryDividerProps = {
  categoryId: string
  subtitle?: string | false
  children: string
}

const LimitBadge = ({ limit, amount }: Pick<Limit, 'limit' | 'amount'>) => {
  const { isMobile, activeMobileTab } = useMobileLayoutStore()

  return (
    <div className={cn(
      'flex items-center justify-center',
      isMobile && activeMobileTab === 'deluxe' && 'col-span-2',
    )}
    >
      <Badge variant="usage" className="text-3xs sm:text-xs md:text-sm">
        {`${amount}/${limit}`}
      </Badge>
    </div>
  )
}

const filterByHubType = (hubId: string, type: HubType) => type === 'control'
  ? CONTROL_HUB_IDS.includes(hubId)
  : DELUXE_HUBS_IDS.includes(hubId)

function CategoryHeader({ categoryId, subtitle, children }: CategoryDividerProps) {
  useWatchMobile()
  const { isMobile, activeMobileTab } = useMobileLayoutStore()

  const limits = useLimitStore(useShallow(
    state => state.limits.filter(limit =>
      (limit.categoryId === categoryId && limit.limit > 0 && limit.limit < 999)
      && (isMobile ? filterByHubType(limit.hubId, activeMobileTab) : true),
    ),
  ))

  return (
    <SectionHeader
      title={children}
      subtitle={subtitle}
      titleClassName={categoryId === '2' && 'line-clamp-2 max-w-[20ch] break-words text-wrap'}
    >
      {limits.length > 0
      && categoryId !== POWER_CATEGORY // ? Invisible limit badges in Power category only in sensors
      && limits.map((limit) => {
        return (
          <LimitBadge
            key={limit.hubId + limit.categoryId}
            amount={limit.amount}
            limit={limit.limit}
          />
        )
      })}
    </SectionHeader>
  )
}

export default CategoryHeader
